import API from '@aws-amplify/api';
import { updateProjectResource } from '@/graphql/mutations';
import { getApiPublic } from '@/graphql/queries';
import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import { getTwilioInfoManagement } from '@/service/twilioService';

/**
 * Save recent outbound call to project settings
 * @param {String} projectId - Project ID
 * @param {String} corporationId - Corporation ID
 * @param {String} userId - User ID
 * @param {Object} callData - Call data object
 * @returns {Promise<Object>} Result object
 */
export const saveRecentOutboundCall = async (projectId, corporationId, userId, callData) => {
    try {
        const result = await API.graphql({
            query: updateProjectResource,
            variables: {
                updateProjectResourceRequestDto: {
                    method: Constant.UPDATE_PROJECT_RESOURCE_METHOD.SAVE_RECENT_OUTBOUND_CALL,
                    projectId,
                    corporationId,
                    userId,
                    callData: JSON.stringify(callData)
                }
            }
        });

        const response = result.data.updateProjectResource;
        return response;
    } catch (error) {
        Logger.error('saveRecentOutboundCall error:', error);
        throw error;
    }
};

/**
 * Get recent outbound calls from project settings
 * @param {String} projectId - Project ID
 * @param {String} corporationId - Corporation ID
 * @param {String} userId - User ID
 * @param {Number} limit - Number of calls to retrieve (default: 3)
 * @returns {Promise<Array>} List of recent calls
 */
export const getRecentOutboundCalls = async (projectId, corporationId, userId, limit = 3) => {
    try {
        const result = await API.graphql({
            query: updateProjectResource,
            variables: {
                updateProjectResourceRequestDto: {
                    method: Constant.UPDATE_PROJECT_RESOURCE_METHOD.GET_RECENT_OUTBOUND_CALLS,
                    projectId,
                    corporationId,
                    userId,
                    limit
                }
            }
        });

        const response = result.data.updateProjectResource;
        
        if (!response || !response.status) {
            return [];
        }
        
        // Parse AWSJSON field (it comes as JSON string from backend)
        let data = [];
        if (response.data) {
            if (typeof response.data === 'string') {
                try {
                    data = JSON.parse(response.data);
                } catch (parseError) {
                    Logger.error('getRecentOutboundCalls: Error parsing data:', parseError);
                    data = [];
                }
            } else if (Array.isArray(response.data)) {
                data = response.data;
            }
        }
        
        return Array.isArray(data) ? data : [];
    } catch (error) {
        Logger.error('getRecentOutboundCalls error:', error);
        return [];
    }
};

/**
 * Validate telephone agent permission for corporation and project
 * @param {String} corporationId - Corporation ID
 * @param {String} projectId - Project ID
 * @returns {Promise<Object>} Validation result with isValid and errorCode
 */
export const validateTelephonePermission = async (corporationId, projectId) => {
    try {
        const params = JSON.stringify({
            getApiType: Constant.GET_API_PUBLIC_TYPE.VALIDATE_TELEPHONE_AGENT_ENABLED,
            corporationId,
            projectId
        });

        const result = await API.graphql({
            query: getApiPublic,
            variables: { params }
        });

        const response = result.data.getApiPublic;
        if (response.success && response.payload) {
            return JSON.parse(response.payload);
        }

        return {
            isValid: false,
            errorCode: 'COMMSG030'
        };
    } catch (error) {
        Logger.error('validateTelephonePermission error:', error);
        return {
            isValid: false,
            errorCode: 'COMMSG030'
        };
    }
};

/**
 * Validate telephone agent permission for training call (supports Public Desk and Private Desk)
 * @param {String} corporationId - Corporation ID
 * @param {String} projectId - Project ID
 * @returns {Promise<Object>} Validation result with isValid and errorCode
 */
export const validateTrainingTelephonePermission = async (corporationId, projectId) => {
    try {
        const params = JSON.stringify({
            getApiType: Constant.GET_API_PUBLIC_TYPE.VALIDATE_TRAINING_TELEPHONE_ENABLED,
            corporationId,
            projectId
        });

        const result = await API.graphql({
            query: getApiPublic,
            variables: { params }
        });

        const response = result.data.getApiPublic;
        if (response.success && response.payload) {
            return JSON.parse(response.payload);
        }

        return {
            isValid: false,
            errorCode: 'COMMSG030'
        };
    } catch (error) {
        Logger.error('validateTrainingTelephonePermission error:', error);
        return {
            isValid: false,
            errorCode: 'COMMSG030'
        };
    }
};

/**
 * Search phone contacts by tab type
 * @param {Object} params
 * @param {String} params.keyword - Search keyword
 * @param {String} params.corporationId - Corporation ID
 * @param {String} params.projectId - Project ID
 * @param {Number} params.limit - Result limit
 * @param {String} params.tabType - Tab type
 * @param {Number} params.page - Page number (0-based)
 * @param {String} params.recipientType - Recipient type for TAB_CONTACTS
 * @returns {Promise<Object>} { data: Array | Object, errorCode?: String }
 */
export const searchPhoneContacts = async (params = {}) => {
    const {
        keyword = '',
        corporationId,
        projectId,
        userId,
        limit = TelephoneConstant.PHONE_SEARCH_PAGE_SIZE,
        tabType = TelephoneConstant.PHONE_SEARCH_TAB.HISTORY,
        page = 0,
        recipientType
    } = params;

    try {
        const requestDto = {
            corporationInuse: corporationId,
            projectInuse: projectId,
            userId: userId,
            actionType: TelephoneConstant.ACTION.SEARCH_PHONE_CONTACTS,
            requestBody: JSON.stringify({
                keyword,
                limit,
                tabType,
                page,
                recipientType
            })
        };
        const resultData = await getTwilioInfoManagement(requestDto);

        if (!resultData || !resultData?.status || resultData?.errorCode) {
            return {
                data: { items: [], totalCount: 0, hasMore: false },
                errorCode: resultData?.errorCode
            };
        }

        return { data: resultData?.data || { items: [], totalCount: 0, hasMore: false } };
    } catch (error) {
        Logger.error('searchPhoneContacts error:', error);
        return { data: { items: [], totalCount: 0, hasMore: false } };
    }
};
